import { useCallback, useRef, useState } from "react";
import type { DragEvent } from "react";

/**
 * Arrastar e soltar arquivos numa área inteira. Devolve `isDragging` (pra mostrar o overlay) e as
 * props a espalhar no container. Só reage a arrasto de ARQUIVOS (texto/links arrastados são ignorados).
 * Usa um contador porque dragenter/dragleave disparam também nos filhos.
 */
export function useFileDrop(onFiles: (files: File[]) => void, disabled = false) {
	const [isDragging, setIsDragging] = useState(false);
	const depth = useRef(0);

	const hasFiles = (e: DragEvent) => Array.from(e.dataTransfer?.types || []).includes("Files");

	const onDragEnter = useCallback(
		(e: DragEvent) => {
			if (disabled || !hasFiles(e)) return;
			e.preventDefault();
			depth.current += 1;
			setIsDragging(true);
		},
		[disabled],
	);
	const onDragOver = useCallback(
		(e: DragEvent) => {
			if (disabled || !hasFiles(e)) return;
			e.preventDefault();
			e.dataTransfer.dropEffect = "copy";
		},
		[disabled],
	);
	const onDragLeave = useCallback((e: DragEvent) => {
		if (!hasFiles(e)) return;
		depth.current = Math.max(0, depth.current - 1);
		if (depth.current === 0) setIsDragging(false);
	}, []);
	const onDrop = useCallback(
		(e: DragEvent) => {
			if (disabled || !hasFiles(e)) return;
			e.preventDefault();
			depth.current = 0;
			setIsDragging(false);
			const files = Array.from(e.dataTransfer.files || []);
			if (files.length) onFiles(files);
		},
		[disabled, onFiles],
	);

	return { isDragging, dropProps: { onDragEnter, onDragOver, onDragLeave, onDrop } };
}

/** Overlay "solte aqui"; o container pai precisa de `relative`. */
export function FileDropOverlay({ visible, label }: { visible: boolean; label: string }) {
	if (!visible) return null;
	return (
		<div className="pointer-events-none absolute inset-0 z-50 flex items-center justify-center rounded-lg border-2 border-dashed border-primary bg-background/80">
			<p className="text-base font-medium text-primary">{label}</p>
		</div>
	);
}
